// DTO ⇄ form values for the Fulfill order page.
import { formValue } from '@/lib/mappers/formValues';
import { unitSymbol } from '@/lib/mappers/units';
import { FACILITY_KINDS, buildFacilityValue } from '@/config/formOptions';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { formatDateOrDash, formatTextOrDash } from '@/lib/format';
import { outgoingShipmentStatusLabel } from '@/config/statuses';
import {
  EMPTY_FULFILL_ORDER_FORM,
  RETIRE_CREDITS,
  SHIPMENT_OPTION,
} from '../constants/fulfillOrder.constants';
import { processOrderDetail, hasWorkingFigures } from './ordersMappers';

// Raw (unformatted) remaining-to-ship / remaining-to-allocate amounts, keyed by unit symbol — the quantity field validates against these, so they must stay real numbers.
const buildRemainingByUnit = (order) => {
  const remaining = {};
  if (hasWorkingFigures(order)) {
    remaining[unitSymbol(order.workingUnit)] = {
      toShip: order.remainingToShipWorkingQuantity ?? null,
      toAllocate: order.remainingToAllocateWorkingQuantity ?? null,
    };
  }
  if (order.declarationUnit) {
    remaining[unitSymbol(order.declarationUnit)] = {
      toShip: order.remainingToShipDeclarationQuantity ?? null,
      toAllocate: order.remainingToAllocateDeclarationQuantity ?? null,
    };
  }
  return remaining;
};

// What is already shipped and allocated — counted UP by this fulfilment, as remainingByUnit counts down.
const buildCommittedByUnit = (order) => {
  const committed = {};
  if (hasWorkingFigures(order)) {
    committed[unitSymbol(order.workingUnit)] = {
      shipped: order.shippedWorkingQuantity ?? null,
      allocated: order.allocatedWorkingQuantity ?? null,
    };
  }
  if (order.declarationUnit) {
    committed[unitSymbol(order.declarationUnit)] = {
      shipped: order.shippedDeclarationQuantity ?? null,
      allocated: order.allocatedDeclarationQuantity ?? null,
    };
  }
  return committed;
};

// One `pendingOrder(id)` node → the Fulfill page's summary + form values, via processOrderDetail so the two screens can't disagree.
export const processOrderForFulfill = (order) => ({
  ...processOrderDetail(order),
  // A back-to-back facility has no terminals, so the field never renders for one.
  hasTerminal: Boolean(order?.terminalRefId),
  // The Select outgoing shipment picker filters `outgoingShipments` on these four.
  linkableMatch: {
    materialId: order?.material?.id ?? null,
    siteId: order?.site?.id ?? null,
    customerAccountId: order?.customerAccount?.id ?? null,
    backToBackAccountId: order?.backToBackAccountId ?? null,
  },
  remainingByUnit: buildRemainingByUnit(order),
  committedByUnit: buildCommittedByUnit(order),
  // TODO: pendingOrder has no transaction-date field yet — a dash until the backend exposes one.
  transactionDate: '-',
  values: {
    ...EMPTY_FULFILL_ORDER_FORM,
    facilityId: order?.site?.id
      ? buildFacilityValue(FACILITY_KINDS.site, order.site.id)
      : order?.backToBackAccountId
        ? buildFacilityValue(FACILITY_KINDS.backToBack, order.backToBackAccountId)
        : '',
    terminalId: formValue(order?.terminalRefId),
  },
});

// The server's assigned is the shipment's total across orders: 0 for an unlinked order, its own only when no other order shares it.
const assignedToOrder = (shipment, orderId, total) => {
  const linked = shipment.linkedOrders ?? [];
  if (orderId == null) return null;
  if (!linked.some((order) => String(order.id) === String(orderId))) return 0;
  return linked.length === 1 ? total : null;
};

const buildLinkableShipmentUnits = (shipment, orderId) => ({
  [UNIT_TYPES.working]: {
    unit: shipment.workingUnit,
    amounts: {
      assignedQuantity: assignedToOrder(shipment, orderId, shipment.assignedQuantityWorking),
      shipmentQuantity: shipment.totalQuantityWorking,
      remainingToAllocateQuantity: shipment.remainingToAllocateQuantityWorking,
      unassignedQuantity: shipment.unassignedQuantityWorking,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: shipment.declarationUnit,
    amounts: {
      assignedQuantity: assignedToOrder(shipment, orderId, shipment.assignedQuantityDeclaration),
      shipmentQuantity: shipment.totalQuantityDeclaration,
      remainingToAllocateQuantity: shipment.remainingToAllocateQuantityDeclaration,
      unassignedQuantity: shipment.unassignedQuantityDeclaration,
    },
  },
});

// One row of the Select outgoing shipment picker — an `outgoingShipments` node.
export const processLinkableOutgoingShipment = (node, orderId) => {
  const units = buildLinkableShipmentUnits(node, orderId);
  const byUnit = buildQuantitiesByUnit(units);
  return {
    id: node.id,
    shipmentId: formatTextOrDash(node.shipmentNo),
    totalOrdersLinked: (node.linkedOrders ?? []).length,
    // Drives the picker's Linked / Other sections; linkedOrders ids are pending-order ids.
    isLinked:
      orderId != null &&
      (node.linkedOrders ?? []).some((order) => String(order.id) === String(orderId)),
    materialName: formatTextOrDash(node.material?.name),
    facilityName: formatTextOrDash(node.site?.siteName ?? node.backToBackAccount?.accountName),
    status: outgoingShipmentStatusLabel(node.shipmentStatus) ?? '',
    soldTo: formatTextOrDash(node.customerAccount?.companyname),
    terminal: formatTextOrDash(node.terminal),
    shippingTo: formatTextOrDash(node.shippingDetails?.shippingTo),
    byUnit,
    // Picking a row writes these numbers into the form; `byUnit` holds display strings.
    rawByUnit: buildRawQuantitiesByUnit(units),
    unitType: defaultUnitType(byUnit),
    dateOfDispatch: formatDateOrDash(node.dispatchDate),
    dateOfDispatchRaw: node.dispatchDate ?? null,
    discrepancyCount: node.discrepancyCount ?? 0,
    // The REST route matched on it server-side; OutgoingShipmentFilters has no such field.
    backToBackAccountId: node.backToBackAccountId ?? null,
    unassignedTotal:
      Number(node.unassignedQuantityWorking ?? 0) + Number(node.unassignedQuantityDeclaration ?? 0),
  };
};

/**
 * The picker's rows. `outgoingShipments` can't express the REST route's last two rules, so
 * the back-to-back match and the has-unassigned-quantity test are applied here.
 */
export const processLinkableShipmentsForFulfill = (
  connection,
  { backToBackAccountId } = {},
  orderId = null,
) =>
  (connection?.nodes ?? [])
    .map((node) => processLinkableOutgoingShipment(node, orderId))
    .filter((row) => row.unassignedTotal > 0)
    .filter((row) => (row.backToBackAccountId ?? null) === (backToBackAccountId ?? null));

// ADD_LATER creates no link, so dispatch neither needs nor validates an assigned quantity.
const LINK_MODES = Object.freeze([SHIPMENT_OPTION.createNew, SHIPMENT_OPTION.selectExisting]);

// Form values → POST /:module/pending-orders/:id/dispatch — no strategy/certificate type travels in this body.
export const buildFulfillOrderDto = (values) => ({
  shipmentMode: values.shipmentOption,
  existingShipmentId: values.existingShipmentId ? Number(values.existingShipmentId) : undefined,
  shipmentNo: [values.idPrefix, values.idNumber].filter(Boolean).join('-') || undefined,
  terminalId: values.terminalId ? Number(values.terminalId) : undefined,
  vesselName: values.vesselName || undefined,
  shipmentQuantity: values.shipmentQuantity ? Number(values.shipmentQuantity) : undefined,
  shipmentQuantityUnit: values.shipmentQuantityUnit || undefined,
  dispatchDate: values.dateOfDispatch || undefined,
  // Required by dispatch for the link modes; there is no server-side default.
  assignedQuantity: LINK_MODES.includes(values.shipmentOption)
    ? Number(values.assignedQuantity)
    : undefined,
  assignedQuantityUnit: LINK_MODES.includes(values.shipmentOption)
    ? values.assignedQuantityUnit || undefined
    : undefined,
  unitType: values.fulfillUnitType || undefined,
  // batchId is a Prisma Int and a GraphQL ID! is a string; transactionId stays alphanumeric text.
  batches: (values.batches ?? []).map((batch) => ({
    batchId: Number(batch.batchId),
    transactionId: batch.transactionId,
    certifiedQuantity: Number(batch.certifiedQuantity),
  })),
  retireCredits: values.retireCredits === RETIRE_CREDITS.yes,
});
